'use client';

import { c as _c } from "react/compiler-runtime";
import { FILE_INPUT_ACCEPT } from '@shared/all/uploadTypes';
import { calculateNewPostTronCostBreakdown } from '@shared/utils/helpers/formatNumbers';
import useUser from '@shared/utils/hooks/useUser';
import { Logo } from '@shared/utils/icons';
import type { Editor } from '@tiptap/react';
import { FileText, Image as ImageIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useTronAffordability } from '../../../hooks/useTronAffordability';
import { imageFilesFrom } from '../../../utils/askImage';
import { NewPostFactiii } from '../../factiiis/NewPostFactiii';
import FormatTools from '../FormatTools';
import { PostInput } from '../LazyPostInput';
import { TOOLBAR_ICON, toolbarButton } from '../toolbarButton';
import UploadGalleryView from '../UploadGalleryView';
import CrossPostIndicator from './CrossPostIndicator';
import { DefaultFactiiiButtons } from './DefaultFactiiiButtons';
import { useNewPostContext } from './NewPostContext';
type Props = {
  extraContent?: ReactNode;
  avatar?: ReactNode;
};
export const NewPostInputSection = t0 => {
  const $ = _c(90);
  const {
    extraContent,
    avatar
  } = t0;
  const {
    title,
    setTitle,
    content,
    setContent,
    rows,
    label,
    uploads,
    uploadingKeys,
    pendingFiles,
    removeAttachmentHandler,
    cameraPhotos,
    removeCameraPhotoHandler,
    factiiiOpen,
    setFactiiiOpen,
    myPostFactiiiCounts,
    setMyPostFactiiiCounts,
    requirementsMet,
    initialLocation,
    onDraftsClick,
    onFilesSelected,
    submitHandler,
    submitDisabled,
    buttonLoading,
    submitButtonText,
    pinnedFactiii,
    queryKey,
    postId,
    isPrivate,
    isOpened,
    toolbarPt: t1,
    isBoardPost
  } = useNewPostContext();
  const toolbarPt = t1 === undefined ? "pt-1" : t1;
  const {
    currentUser
  } = useUser();
  const fileInputRef = useRef(null);
  const [editor, setEditor] = useState(null);
  const costBreakdown = isPrivate ? {
    tagEditFee: 0,
    botCost: 0,
    total: 0
  } : calculateNewPostTronCostBreakdown(myPostFactiiiCounts);
  const {
    insufficientTrons,
    poolCoversRemainder
  } = useTronAffordability({
    tagEditFee: costBreakdown.tagEditFee,
    botCost: costBreakdown.botCost,
    awardCost: 0
  });
  let t2;
  if ($[0] !== onFilesSelected) {
    t2 = e => {
      const files = imageFilesFrom(e.clipboardData);
      if (files.length > 0) {
        e.preventDefault();
        onFilesSelected(files);
      }
    };
    $[0] = onFilesSelected;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handlePaste = t2;
  let t3;
  if ($[2] !== onFilesSelected) {
    t3 = e_0 => {
      const files_0 = Array.from(e_0.target.files || []);
      if (files_0.length > 0) {
        onFilesSelected(files_0);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
    $[2] = onFilesSelected;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleFileChange = t3;
  let t4;
  if ($[4] !== avatar) {
    t4 = avatar && <div className="mb-2">{avatar}</div>;
    $[4] = avatar;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== content || $[7] !== isBoardPost || $[8] !== label || $[9] !== rows || $[10] !== setContent || $[11] !== setTitle || $[12] !== title) {
    t5 = isBoardPost ? <div className="space-y-3"><div><p className="mb-1.5 text-xs font-emphasis text-textSecondary">Title</p><input type="text" value={title} onChange={e_1 => setTitle(e_1.target.value)} placeholder="Enter a title for this card..." maxLength={255} autoFocus={true} className="w-full rounded-lg border border-ds-border-standard bg-background-secondary px-3 py-2.5 text-base font-emphasis text-textPrimary outline-none transition-colors duration-200 placeholder:text-textSecondary/40 focus:border-primary" /></div><div><p className="mb-1.5 text-xs font-emphasis text-textSecondary">Description{" "}<span className="font-normal text-textSecondary/50">(optional)</span></p><div className="w-full min-w-0 rounded-lg border border-ds-border-standard bg-background-secondary px-1 transition-colors duration-200 focus-within:border-primary"><PostInput setContent={setContent} content={content} rows={3} label="Add more detail..." onEditor={setEditor} /></div></div></div> : <div className="w-full min-w-0"><PostInput setContent={setContent} content={content} rows={rows} label={label} onEditor={setEditor} /></div>;
    $[6] = content;
    $[7] = isBoardPost;
    $[8] = label;
    $[9] = rows;
    $[10] = setContent;
    $[11] = setTitle;
    $[12] = title;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== cameraPhotos || $[15] !== pendingFiles || $[16] !== removeAttachmentHandler || $[17] !== removeCameraPhotoHandler || $[18] !== uploadingKeys || $[19] !== uploads) {
    t6 = (uploads.length > 0 || cameraPhotos.length > 0 || pendingFiles.length > 0) && <UploadGalleryView uploads={uploads} removeAttachmentHandler={removeAttachmentHandler} cameraPhotos={cameraPhotos} removeCameraPhotoHandler={removeCameraPhotoHandler} uploadingKeys={uploadingKeys} pendingFiles={pendingFiles} />;
    $[14] = cameraPhotos;
    $[15] = pendingFiles;
    $[16] = removeAttachmentHandler;
    $[17] = removeCameraPhotoHandler;
    $[18] = uploadingKeys;
    $[19] = uploads;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== handleFileChange) {
    t7 = <input ref={fileInputRef} type="file" multiple={true} accept={FILE_INPUT_ACCEPT} onChange={handleFileChange} className="hidden" />;
    $[21] = handleFileChange;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = `flex w-full min-w-0 flex-wrap items-center gap-x-1 gap-y-2 px-1 ${toolbarPt} pb-2`;
  let t10;
  let t9;
  if ($[23] !== editor) {
    t9 = <FormatTools editor={editor} />;
    t10 = editor && <span className="mx-0.5 h-5 w-px shrink-0 bg-ds-border-standard" />;
    $[23] = editor;
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <div className="flex grow items-center justify-between gap-0.5">{t9}{t10}</div>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== factiiiOpen) {
    t12 = toolbarButton(factiiiOpen);
    $[29] = factiiiOpen;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== setFactiiiOpen) {
    t13 = () => setFactiiiOpen(_temp);
    $[31] = setFactiiiOpen;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Logo height={TOOLBAR_ICON} rounded={true} color="currentColor" bgColor="transparent" />;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t13) {
    t15 = <button type="button" title="Close Factiiis" className={t12} onClick={t13}>{t14}</button>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== initialLocation || $[38] !== myPostFactiiiCounts || $[39] !== requirementsMet || $[40] !== setMyPostFactiiiCounts) {
    t16 = <DefaultFactiiiButtons myPostFactiiiCounts={myPostFactiiiCounts} setMyPostFactiiiCounts={setMyPostFactiiiCounts} setNsfw={_temp2} setPolitics={_temp3} requirementsMet={requirementsMet} initialLocation={initialLocation} />;
    $[37] = initialLocation;
    $[38] = myPostFactiiiCounts;
    $[39] = requirementsMet;
    $[40] = setMyPostFactiiiCounts;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <button type="button" title="Upload Media" className={toolbarButton()} onClick={() => fileInputRef.current?.click()}><ImageIcon size={TOOLBAR_ICON} /></button>;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = toolbarButton();
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <FileText size={TOOLBAR_ICON} />;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== onDraftsClick) {
    t20 = <button type="button" title="Drafts" className={t18} onClick={onDraftsClick}>{t19}</button>;
    $[45] = onDraftsClick;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== currentUser.isAuthenticated) {
    t21 = currentUser.isAuthenticated && <CrossPostIndicator />;
    $[47] = currentUser.isAuthenticated;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== buttonLoading || $[50] !== submitButtonText) {
    t22 = buttonLoading ? <div className="loader" /> : submitButtonText;
    $[49] = buttonLoading;
    $[50] = submitButtonText;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== submitDisabled || $[53] !== submitHandler || $[54] !== t22) {
    t23 = <button type="button" className="ml-auto flex min-w-[72px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50" onClick={submitHandler} disabled={submitDisabled}>{t22}</button>;
    $[52] = submitDisabled;
    $[53] = submitHandler;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t15 || $[57] !== t16 || $[58] !== t20 || $[59] !== t21 || $[60] !== t23) {
    t24 = <div className="flex min-w-0 grow items-center justify-between gap-0.5">{t15}{t16}{t17}{t20}{t21}{t23}</div>;
    $[56] = t15;
    $[57] = t16;
    $[58] = t20;
    $[59] = t21;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t11 || $[63] !== t24 || $[64] !== t8) {
    t25 = <div className={t8}>{t11}{t24}</div>;
    $[62] = t11;
    $[63] = t24;
    $[64] = t8;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  const t26 = factiiiOpen && costBreakdown.total > 0 && <div className={`flex items-center justify-between gap-2 px-1 py-2 text-xs ${insufficientTrons ? "text-destructive" : "text-textSecondary"}`}><span>Cost: <strong>{costBreakdown.total.toLocaleString()}</strong> trons</span><span className="truncate text-right">Balance:{" "}<strong>{currentUser.tronsBalance.toLocaleString()}</strong>{insufficientTrons && " \u2014 insufficient"}{poolCoversRemainder && <span className="text-primary"> — pool covers rest</span>}</span></div>;
  const t27 = factiiiOpen ? "1fr" : "0fr";
  let t28;
  if ($[66] !== t27) {
    t28 = {
      gridTemplateRows: t27
    };
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== isOpened || $[69] !== isPrivate || $[70] !== myPostFactiiiCounts || $[71] !== pinnedFactiii || $[72] !== postId || $[73] !== queryKey || $[74] !== requirementsMet || $[75] !== setMyPostFactiiiCounts) {
    t29 = <div className="min-h-0 overflow-hidden"><div className="max-h-[45vh] overflow-y-auto"><NewPostFactiii requirementsMet={requirementsMet} postId={postId} pinnedFactiii={pinnedFactiii} myPostFactiiiCounts={myPostFactiiiCounts} setMyPostFactiiiCounts={setMyPostFactiiiCounts} setNsfw={_temp4} setPolitics={_temp5} queryKey={queryKey} isPrivate={isPrivate} isOpened={isOpened} costInFooter={true} /></div></div>;
    $[68] = isOpened;
    $[69] = isPrivate;
    $[70] = myPostFactiiiCounts;
    $[71] = pinnedFactiii;
    $[72] = postId;
    $[73] = queryKey;
    $[74] = requirementsMet;
    $[75] = setMyPostFactiiiCounts;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[77] !== t28 || $[78] !== t29) {
    t30 = <div className="grid transition-[grid-template-rows] duration-300 ease-out" style={t28}>{t29}</div>;
    $[77] = t28;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== extraContent || $[81] !== handlePaste || $[82] !== t25 || $[83] !== t26 || $[84] !== t30 || $[85] !== t4 || $[86] !== t5 || $[87] !== t6 || $[88] !== t7) {
    t31 = <div onPaste={handlePaste}>{t4}{t5}{extraContent}{t6}{t7}{t25}{t26}{t30}</div>;
    $[80] = extraContent;
    $[81] = handlePaste;
    $[82] = t25;
    $[83] = t26;
    $[84] = t30;
    $[85] = t4;
    $[86] = t5;
    $[87] = t6;
    $[88] = t7;
    $[89] = t31;
  } else {
    t31 = $[89];
  }
  return t31;
};
function _temp(prev) {
  return !prev;
}
function _temp2() {}
function _temp3() {}
function _temp4() {}
function _temp5() {}